import React, { useEffect, useState } from 'react';
import { useGLTF } from '@react-three/drei';
// Track which models have already been preloaded
const PRELOADED_MODELS = new Set<string>();
const PRELOADED_SOUNDS = new Set<string>();
const PRELOADED_IMAGES = new Set<string>();

interface AssetPreloaderProps {
  onProgress?: (progress: number) => void;
  onComplete?: () => void;
}

/**
 * Component to preload game assets in the background.
 * This can be mounted at application start to begin loading assets.
 */
const AssetPreloader: React.FC<AssetPreloaderProps> = ({ 
  onProgress, 
  onComplete 
}) => {
  const [progress, setProgress] = useState(0);
  
  useEffect(() => {
    // Don't run again if we've already started preloading
    if (PRELOADED_MODELS.size > 0) return;
    
    //console.log("Starting initial asset preloading...");
    
    // Keep startup preloading intentionally small.
    // The match interstitial preloads selected fighters just before gameplay.
    const modelsToPreload = [
      '/backgroundglbs/skeletor.glb',
      '/pointingmeme.glb'
    ];
    
    // Collect sounds to preload
    const soundsToPreload = [
      '/sounds/readyfight.mp3',
      '/sounds/fight.mp3',
      '/sounds/jump.mp3',
      '/sounds/dogepunch.mp3',
      '/sounds/pepepunch.mp3',
      '/sounds/trollfacepunch.mp3',
      '/sounds/wifejakpunch.mp3',
      '/sounds/gigachadpunch.mp3',
    ];
    
    // Images to preload
    const imagesToPreload = [
      '/cam.png',
      '/coverOLDDD.png',
      '/pfps/dogepfp.webp',
      '/pfps/pepepfp.webp',
      '/pfps/trollfacepfp.webp',
      '/pfps/wifejakpfp.webp',
      '/pfps/grumpycatpfp.webp',
      '/pfps/moodengpfp.webp',
      '/pfps/gigachadpfp.webp',
    ];
    
    // Total items to track progress
    const totalItems = modelsToPreload.length + soundsToPreload.length + imagesToPreload.length;
    let loadedItems = 0;
    const audioCleanups: Array<() => void> = [];
    
    // Update progress function
    const updateProgress = () => {
      loadedItems++;
      const progressPercent = Math.floor((loadedItems / totalItems) * 100);
      setProgress(progressPercent);
      if (onProgress) onProgress(progressPercent);
      
      // Check if complete
      if (loadedItems >= totalItems) {
      //  console.log("Initial preloading complete!");
        if (onComplete) onComplete();
      }
    };
    
    // Start preloading models
    modelsToPreload.forEach(path => {
      try {
        if (PRELOADED_MODELS.has(path)) {
          updateProgress();
          return;
        }
        PRELOADED_MODELS.add(path);
        useGLTF.preload(path);
        updateProgress();
      } catch (err) {
        console.error(`Error preloading model: ${path}`, err);
        updateProgress(); // Still count it to avoid getting stuck
      }
    });
    
    // Preload sounds
    soundsToPreload.forEach(sound => {
      if (!PRELOADED_SOUNDS.has(sound)) {
        PRELOADED_SOUNDS.add(sound);
        const audio = new Audio(sound);
        const handleCanPlayThrough = () => {
          updateProgress();
        };
        const handleError = () => {
          console.error(`Error loading sound: ${sound}`);
          updateProgress();
        };
        audio.addEventListener('canplaythrough', handleCanPlayThrough, { once: true });
        audio.addEventListener('error', handleError, { once: true });
        audioCleanups.push(() => {
          audio.removeEventListener('canplaythrough', handleCanPlayThrough);
          audio.removeEventListener('error', handleError);
          audio.pause();
          audio.src = '';
        });
        audio.load();
      } else {
        updateProgress();
      }
    });
    
    // Preload images
    imagesToPreload.forEach(imgSrc => {
      if (!PRELOADED_IMAGES.has(imgSrc)) {
        PRELOADED_IMAGES.add(imgSrc);
        const img = new Image();
        img.onload = () => {
          updateProgress();
        };
        img.onerror = () => {
          console.error(`Error loading image: ${imgSrc}`);
          updateProgress();
        };
        img.src = imgSrc;
      } else {
        updateProgress();
      }
    });
    
    // Safety timeout to ensure complete callback is called
    const safetyTimeout = setTimeout(() => {
      if (loadedItems < totalItems) {
        console.log(`Safety timeout reached. Loaded ${loadedItems}/${totalItems} items.`);
        if (onComplete) onComplete();
      }
    }, 8000); // 8 second timeout
    
    return () => {
      clearTimeout(safetyTimeout);
      audioCleanups.forEach(cleanup => cleanup());
    };
  }, [onProgress, onComplete]);
  
  // This component doesn't render anything visible
  return null;
};

export default AssetPreloader;
